import axios, { AxiosResponse } from "axios";
import Cookies from "js-cookie";
import Swal from "sweetalert2";

const ROOT_URL = "https://wbrso.transport-community.org/api/";
//const ROOT_URL = "http://localhost:8080/api/";
const HEADERS = {
  "Content-Type": "application/json",
  Accept: "application/json",
};

const api = axios.create({
    baseURL: ROOT_URL,
    headers: HEADERS,
});

api.interceptors.request.use((config) => {
    const token = Cookies.get("token");
    if(token){
        config.headers = config.headers ?? {};
        config.headers.Authorization = `Bearer ${token}`
    }
    return config;
});

api.interceptors.response.use(
    (response) => {
        const message = response?.data?.message;
        if (message) {
            Swal.fire({
                text: message,
                timer: 3000,
                toast: true,
                position: "top-end",
                showConfirmButton: false,
                timerProgressBar: true,
                    didOpen: (toast) => {
                        toast.addEventListener('mouseenter', Swal.stopTimer)
                        toast.addEventListener('mouseleave', Swal.resumeTimer)
                    }
            });
        }
       return response;  
    },
    (error) => {
        const message = 
            error?.response?.data?.message ||
            error?.response?.data?.error || 
            "An error occurred.";

        Swal.fire({
            icon: "error",
            title: "Error",
            text: message,
            timer: 5000,
            toast: true,
            position: "top-end",
            showConfirmButton: false,
        });
        
        return Promise.reject(error);
    }
);

/**
 * Asset type for the endpoints
 */
export type AssetType = "roads" | "railways";

/**
 * Raw API shapes (partial) for roads / railways endpoints
 */
export interface ApiEventRoads {
  id?: number;
  npmRoadId?: number;
  routeId?: string;
  eventType?: string;
  startDate?: string | null;
  endDate?: string | null;
  startLatitude?: number | null;
  startLongitude?: number | null;
  endLatitude?: number | null;
  endLongitude?: number | null;
  [key: string]: any;
}

export interface ApiEventRailways {
  id?: number;
  npmRwId?: number;
  routeId?: string;
  eventType?: string;
  startDate?: string | null;
  endDate?: string | null;
  startLatitude?: number | null;
  startLongitude?: number | null;
  endLatitude?: number | null;
  endLongitude?: number | null;
  [key: string]: any;
}

/**
 * Normalized event returned to the frontend
 */
export interface NormalizedEvent {
  id: number | null;
  npmId: number | null; // generic id (road or railway)
  routeId: string;
  eventType: string;
  startDate: string | null; // 'YYYY-MM-DD' or null
  endDate: string | null;
  startLatitude: number | null;
  startLongitude: number | null;
  endLatitude: number | null;
  endLongitude: number | null;
}

/**
 * Payload accepted by add/edit functions.
 * Both npmRoadId and npmRwId are optional here so the function can set them based on assetType.
 */
export interface EventPayload {
  id?: number;
  npmId?: number;
  npmRoadId?: number;
  npmRwId?: number;
  routeId?: string;
  eventType?: string;
  startDate?: string | null;
  endDate?: string | null;
  startLatitude?: number | null;
  startLongitude?: number | null;
  endLatitude?: number | null;
  endLongitude?: number | null;
  // allow the form fields names too
  startPointLat?: string | number | null;
  startPointLon?: string | number | null;
  endPointLat?: string | number | null;
  endPointLon?: string | number | null;
  [key: string]: any;
}

/* Helpers for endpoints */
function basePathFor(assetType: AssetType): string {
  return assetType === "railways" ? "railways_npm_climate_event" : "roads_npm_climate_event";
}
function getListUrl(assetType: AssetType, npmId: number): string {
  return `${ROOT_URL}${basePathFor(assetType)}/getByNpmId?npmId=${npmId}`;
}
function getAddUrl(assetType: AssetType): string {
  return `${ROOT_URL}${basePathFor(assetType)}/add`;
}
function getEditUrl(assetType: AssetType): string {
  return `${ROOT_URL}${basePathFor(assetType)}/edit`;
}
function getDeleteUrl(assetType: AssetType, id: number): string {
  return `${ROOT_URL}${basePathFor(assetType)}/delete?id=${id}`;
}

/**
 * Normalize a backend object (roads or railways) to NormalizedEvent
 */
function normalize(apiObj: ApiEventRoads | ApiEventRailways | any): NormalizedEvent {
  const npmId = apiObj?.npmRwId ?? apiObj?.npmRoadId ?? apiObj?.npmId ?? null;
  return {
    id: apiObj?.id ?? null,
    npmId,
    routeId: apiObj?.routeId ?? "",
    eventType: apiObj?.eventType ?? "",
    startDate: apiObj?.startDate ?? null,
    endDate: apiObj?.endDate ?? null,
    startLatitude: apiObj?.startLatitude ?? null,
    startLongitude: apiObj?.startLongitude ?? null,
    endLatitude: apiObj?.endLatitude ?? null,
    endLongitude: apiObj?.endLongitude ?? null,
  };
}

/**
 * Fetch and return an array of NormalizedEvent
 */
export async function getByNpmId(npmId: number, assetType: AssetType = "roads"): Promise<NormalizedEvent[]> {
  try {
    const resp: AxiosResponse = await api.get(getListUrl(assetType, npmId), { headers: HEADERS });
    const raw = resp.data?.data ?? resp.data ?? [];
    if (!Array.isArray(raw)) return [];
    return raw.map((r: any) => normalize(r));
  } catch (err) {
    console.error("Error fetching climate events:", err);
    throw err;
  }
}

/**
 * Build base payload for add/edit and map form field names to backend numeric fields.
 * Returns a payload object that includes optional npmRoadId/npmRwId (so TypeScript won't complain).
 */
function buildPayload(eventData: EventPayload, assetType: AssetType): EventPayload {
  const startLat =
    eventData.startLatitude ??
    (eventData.startPointLat !== undefined && eventData.startPointLat !== null ? parseFloat(String(eventData.startPointLat)) : undefined);
  const startLon =
    eventData.startLongitude ??
    (eventData.startPointLon !== undefined && eventData.startPointLon !== null ? parseFloat(String(eventData.startPointLon)) : undefined);
  const endLat =
    eventData.endLatitude ??
    (eventData.endPointLat !== undefined && eventData.endPointLat !== null ? parseFloat(String(eventData.endPointLat)) : undefined);
  const endLon =
    eventData.endLongitude ??
    (eventData.endPointLon !== undefined && eventData.endPointLon !== null ? parseFloat(String(eventData.endPointLon)) : undefined);

  const payload: EventPayload = {
    // copy known fields
    id: eventData.id,
    routeId: eventData.routeId,
    eventType: eventData.eventType,
    startDate: eventData.startDate ?? undefined,
    endDate: eventData.endDate ?? undefined,
    startLatitude: Number.isFinite(startLat as number) ? (startLat as number) : startLat,
    startLongitude: Number.isFinite(startLon as number) ? (startLon as number) : startLon,
    endLatitude: Number.isFinite(endLat as number) ? (endLat as number) : endLat,
    endLongitude: Number.isFinite(endLon as number) ? (endLon as number) : endLon,
  };

  // Map generic npmId to the correct backend field(s)
  // If you need both fields present in the same request, set both below.
  if (assetType === "railways") {
    payload.npmRwId = eventData.npmId ?? eventData.npmRwId ?? undefined;
  } else {
    payload.npmRoadId = eventData.npmId ?? eventData.npmRoadId ?? undefined;
  }

  return payload;
}

/**
 * Add new event.
 * Sends payload containing npmRoadId or npmRwId depending on assetType.
 */
export async function addEvent(eventData: EventPayload, assetType: AssetType = "roads"): Promise<any> {
  try {
    const payload = buildPayload(eventData, assetType);
    const resp = await api.post(getAddUrl(assetType), payload, { headers: HEADERS });
    return resp.data;
  } catch (err) {
    console.error("Error adding climate event:", err);
    throw err;
  }
}

/**
 * Edit existing event. eventData must contain id.
 */
export async function editEvent(eventData: EventPayload, assetType: AssetType = "roads"): Promise<any> {
  try {
    const payload = buildPayload(eventData, assetType);
    const resp = await api.post(getEditUrl(assetType), payload, { headers: HEADERS });
    return resp.data;
  } catch (err) {
    console.error("Error editing climate event:", err);
    throw err;
  }
}

/**
 * Delete event by id
 */
export async function deleteEvent(id: number, assetType: AssetType = "roads"): Promise<any> {
  try {
    const resp = await api.post(getDeleteUrl(assetType, id), {}, { headers: HEADERS });
    return resp.data;
  } catch (err) {
    console.error("Error deleting climate event:", err);
    throw err;
  }
}

/* Convenience wrappers */
export const getRoadsByNpmId = (npmId: number) => getByNpmId(npmId, "roads");
export const getRailwaysByNpmId = (npmId: number) => getByNpmId(npmId, "railways");
export const addRoadEvent = (payload: EventPayload) => addEvent(payload, "roads");
export const addRailEvent = (payload: EventPayload) => addEvent(payload, "railways");
export const editRoadEvent = (payload: EventPayload) => editEvent(payload, "roads");
export const editRailEvent = (payload: EventPayload) => editEvent(payload, "railways");
export const deleteRoadEvent = (id: number) => deleteEvent(id, "roads");
export const deleteRailEvent = (id: number) => deleteEvent(id, "railways");

/* Default export for backward compatibility (old code expecting default service) */
const defaultExport = {
  getByNpmId: (npmId: number) => getByNpmId(npmId, "roads"),
  addEvent: (payload: EventPayload) => addEvent(payload, "roads"),
  editEvent: (payload: EventPayload) => editEvent(payload, "roads"),
  deleteEvent: (id: number) => deleteEvent(id, "roads"),
};

export default defaultExport;